/**
 * @fileoverview Project Part Detail Page
 *
 * Dedicated page for viewing and editing a project part.
 * Replaces the modal-based approach with a full page for better navigation context.
 *
 * Features:
 * - Navigation context (breadcrumb, prev/next part cycling)
 * - Basic info editing (title, label, description)
 * - Financial overview
 * - Planning (dates)
 * - Supplier management with RFP flow
 * - Budget assignment from investment lines
 *
 * @module RenovationProject/ProjectPartDetailPage
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  CaretLeft,
  CaretRight,
  CaretDown,
  CaretUp,
  Trash,
  FloppyDisk,
  Spinner,
  CheckCircle,
  Circle,
  Clock,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ProjectPart,
  RenovationProject,
  PartStatus,
} from "shared/types/renovation";

// Section components (to be extracted)
import { PartBasicInfoSection } from "./components/sections/PartBasicInfoSection";
import { PartFinancialSection } from "./components/sections/PartFinancialSection";
import { ProvisionalUnitsSection } from "./components/sections/ProvisionalUnitsSection";
import { PartPlanningSection } from "./components/sections/PartPlanningSection";
import { PartSuppliersSection } from "./components/sections/PartSuppliersSection";
import { PartBudgetAssignmentSection } from "./components/sections/PartBudgetAssignmentSection";
import { PartFinancialDocumentsSection } from "./customSections/PartFinancialDocumentsSection";

// Get status display info
const getStatusInfo = (status: PartStatus | undefined) => {
  switch (status) {
    case PartStatus.COMPLETED:
      return {
        icon: CheckCircle,
        color: "text-green-600",
        bgColor: "bg-green-50",
        borderColor: "border-green-200",
        label: "Afgerond",
      };
    case PartStatus.IN_PROGRESS:
      return {
        icon: Clock,
        color: "text-blue-600",
        bgColor: "bg-blue-50",
        borderColor: "border-blue-200",
        label: "In uitvoering",
      };
    case PartStatus.NOT_STARTED:
    default:
      return {
        icon: Circle,
        color: "text-neutral-400",
        bgColor: "bg-neutral-50",
        borderColor: "border-neutral-200",
        label: "Niet gestart",
      };
  }
};

// Format date helper
const formatDate = (date: Date | string | undefined) => {
  if (!date) return "";
  if (date instanceof Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  return typeof date === "string" ? date.split("T")[0] : "";
};

export const ProjectPartDetailPage: React.FC = () => {
  const { projectId, partId } = useParams<{
    projectId: string;
    partId: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { session } = useSession();
  const buildingId = searchParams.get("buildingId");

  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Project and part data
  const [project, setProject] = useState<RenovationProject | null>(null);
  const [part, setPart] = useState<ProjectPart | null>(null);
  const [originalPart, setOriginalPart] = useState<ProjectPart | null>(null);

  // Navigation state
  const [allParts, setAllParts] = useState<ProjectPart[]>([]);
  const [currentPartIndex, setCurrentPartIndex] = useState<number>(-1);

  // Delete confirmation dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Parts overview expansion
  const [isPartsOverviewExpanded, setIsPartsOverviewExpanded] = useState(false);

  // Check for unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!part || !originalPart) return false;
    return JSON.stringify(part) !== JSON.stringify(originalPart);
  }, [part, originalPart]);

  // Fetch project and part data
  const fetchData = useCallback(async () => {
    if (!session || !projectId || !partId) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch project with all parts for navigation context
      const projectResponse = await fetchWithAuth(
        `${API_URL}/renovation/project/${projectId}`,
        { method: "GET" },
        session
      );

      if (!projectResponse.ok) {
        throw new Error("Failed to fetch project");
      }

      const projectData: RenovationProject = await projectResponse.json();
      setProject(projectData);

      // Find the part in the project
      const parts = projectData.parts || [];
      setAllParts(parts);

      const partIndex = parts.findIndex((p) => p.id === Number(partId));
      if (partIndex === -1) {
        throw new Error("Part not found in project");
      }

      setCurrentPartIndex(partIndex);

      // Fetch detailed part data
      const partResponse = await fetchWithAuth(
        `${API_URL}/renovation/project/part/${partId}`,
        { method: "GET" },
        session
      );

      if (!partResponse.ok) {
        throw new Error("Failed to fetch part details");
      }

      const partData: ProjectPart = await partResponse.json();

      // Normalize dates
      const normalizedPart = {
        ...partData,
        plannedStartDate: partData.plannedStartDate
          ? formatDate(partData.plannedStartDate)
          : undefined,
        plannedEndDate: partData.plannedEndDate
          ? formatDate(partData.plannedEndDate)
          : undefined,
      } as ProjectPart;

      setPart(normalizedPart);
      setOriginalPart(normalizedPart);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err instanceof Error ? err.message : "Failed to load data");
      toast.error("Fout bij ophalen van gegevens");
    } finally {
      setIsLoading(false);
    }
  }, [session, projectId, partId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle field changes
  const handleFieldChange = useCallback((field: string, value: unknown) => {
    setPart((prev) => (prev ? { ...prev, [field]: value } : null));
  }, []);

  // Handle save
  const handleSave = async () => {
    if (!part || !session) return;

    setIsSaving(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/part/${part.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: part.title,
            description: part.description,
            labelId: part.label?.id ?? null,
            plannedStartDate: part.plannedStartDate
              ? formatDate(part.plannedStartDate)
              : null,
            plannedEndDate: part.plannedEndDate
              ? formatDate(part.plannedEndDate)
              : null,
            // Note: budgetAssignments removed - budget now managed via investment item assignments
          }),
        },
        session
      );

      if (!response.ok) {
        throw new Error("Failed to save part");
      }

      const updatedPart = await response.json();

      // Normalize dates in response
      const normalizedPart = {
        ...updatedPart,
        plannedStartDate: updatedPart.plannedStartDate
          ? formatDate(updatedPart.plannedStartDate)
          : undefined,
        plannedEndDate: updatedPart.plannedEndDate
          ? formatDate(updatedPart.plannedEndDate)
          : undefined,
      } as ProjectPart;

      setPart(normalizedPart);
      setOriginalPart(normalizedPart);
      toast.success("Projectonderdeel opgeslagen");
    } catch (err) {
      console.error("Error saving part:", err);
      toast.error("Fout bij opslaan projectonderdeel");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!part || !session) return;

    setIsDeleting(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/part/${part.id}`,
        { method: "DELETE" },
        session
      );

      if (!response.ok) {
        throw new Error("Failed to delete part");
      }

      toast.success("Projectonderdeel verwijderd");

      // Navigate back to project
      navigateToProject();
    } catch (err) {
      console.error("Error deleting part:", err);
      toast.error("Fout bij verwijderen projectonderdeel");
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // Navigation helpers
  const navigateToProject = useCallback(() => {
    const params = buildingId ? `?buildingId=${buildingId}` : "";
    navigate(`/renovation/${projectId}${params}`);
  }, [navigate, projectId, buildingId]);

  const navigateToPart = useCallback(
    (targetPartId: number) => {
      const params = buildingId ? `?buildingId=${buildingId}` : "";
      navigate(`/renovation/${projectId}/part/${targetPartId}${params}`);
    },
    [navigate, projectId, buildingId]
  );

  const goToPreviousPart = useCallback(() => {
    if (currentPartIndex > 0) {
      navigateToPart(allParts[currentPartIndex - 1].id);
    }
  }, [currentPartIndex, allParts, navigateToPart]);

  const goToNextPart = useCallback(() => {
    if (currentPartIndex < allParts.length - 1) {
      navigateToPart(allParts[currentPartIndex + 1].id);
    }
  }, [currentPartIndex, allParts, navigateToPart]);

  // Refresh part data (for supplier updates, etc.)
  const refreshPartData = useCallback(async () => {
    if (!session || !partId) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/part/${partId}`,
        { method: "GET" },
        session
      );

      if (response.ok) {
        const partData: ProjectPart = await response.json();
        const normalizedPart = {
          ...partData,
          plannedStartDate: partData.plannedStartDate
            ? formatDate(partData.plannedStartDate)
            : undefined,
          plannedEndDate: partData.plannedEndDate
            ? formatDate(partData.plannedEndDate)
            : undefined,
        } as ProjectPart;
        setPart(normalizedPart);
        setOriginalPart(normalizedPart);
      }
    } catch (err) {
      console.error("Error refreshing part data:", err);
    }
  }, [session, partId]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex items-center gap-3">
          <Spinner className="h-8 w-8 animate-spin text-moss-primary" />
          <span className="text-neutral-secondary-mid">Laden...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !part || !project) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-neutral-primary">
            {error || "Projectonderdeel niet gevonden"}
          </h2>
          <p className="mt-2 text-sm text-neutral-secondary-mid">
            Het projectonderdeel kon niet worden geladen.
          </p>
        </div>
        <Button variant="outline" onClick={navigateToProject}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Terug naar project
        </Button>
      </div>
    );
  }

  const canGoPrevious = currentPartIndex > 0;
  const canGoNext = currentPartIndex < allParts.length - 1;

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Navigation Header */}
      <div className="sticky top-0 z-40 border-b border-neutral-200 bg-white/95 backdrop-blur-sm">
        <div className="mx-auto max-w-7xl px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left: Back to project + Breadcrumb */}
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={navigateToProject}
                className="text-neutral-secondary-mid hover:text-neutral-primary"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Terug naar project
              </Button>

              <div className="hidden items-center gap-2 text-sm text-neutral-secondary-mid md:flex">
                <span className="font-medium text-neutral-primary">
                  RENO-{projectId}
                </span>
                <span>/</span>
                <span>Onderdeel: {part.title || "Naamloos"}</span>
              </div>
            </div>

            {/* Right: Part navigation */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-secondary-mid">
                Onderdeel {currentPartIndex + 1} van {allParts.length}
              </span>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToPreviousPart}
                  disabled={!canGoPrevious}
                  title="Vorige onderdeel"
                >
                  <CaretLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToNextPart}
                  disabled={!canGoNext}
                  title="Volgende onderdeel"
                >
                  <CaretRight className="h-4 w-4" />
                </Button>
              </div>

              {/* Expand/collapse button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsPartsOverviewExpanded(!isPartsOverviewExpanded)}
                className="text-neutral-secondary-mid hover:text-neutral-primary"
              >
                {isPartsOverviewExpanded ? (
                  <>
                    <CaretUp className="mr-1 h-4 w-4" />
                    Verberg overzicht
                  </>
                ) : (
                  <>
                    <CaretDown className="mr-1 h-4 w-4" />
                    Toon alle onderdelen
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Expandable Parts Overview */}
        {isPartsOverviewExpanded && (
          <div className="border-b border-neutral-200 bg-neutral-50/50">
            <div className="mx-auto max-w-5xl px-4 py-4">
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {allParts.map((p, index) => {
                  const statusInfo = getStatusInfo(p.status);
                  const StatusIcon = statusInfo.icon;
                  const isCurrentPart = p.id === part?.id;

                  return (
                    <button
                      key={p.id}
                      onClick={() => {
                        if (!isCurrentPart) {
                          const params = buildingId ? `?buildingId=${buildingId}` : "";
                          navigate(`/renovation/${projectId}/part/${p.id}${params}`);
                        }
                      }}
                      className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-all ${
                        isCurrentPart
                          ? "border-moss-primary bg-moss-primary/5 ring-1 ring-moss-primary"
                          : "border-neutral-200 bg-white hover:border-moss-primary/50 hover:bg-moss-primary/5"
                      }`}
                    >
                      <div className={`mt-0.5 rounded-full p-1 ${statusInfo.bgColor}`}>
                        <StatusIcon
                          size={14}
                          weight="fill"
                          className={statusInfo.color}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-neutral-400">
                            #{index + 1}
                          </span>
                          {isCurrentPart && (
                            <Badge className="h-4 bg-moss-primary px-1.5 text-[10px] text-white">
                              Huidig
                            </Badge>
                          )}
                        </div>
                        <p
                          className={`truncate text-sm font-medium ${
                            isCurrentPart ? "text-moss-primary" : "text-neutral-primary"
                          }`}
                        >
                          {p.title || "Naamloos onderdeel"}
                        </p>
                        <div className="mt-1 flex items-center gap-2">
                          {p.label && (
                            <Badge
                              variant="outline"
                              className="h-5 border-moss-primary/30 bg-moss-primary/5 px-1.5 text-[10px] text-moss-primary"
                            >
                              {p.label.name}
                            </Badge>
                          )}
                          <span className="text-[10px] text-neutral-400">
                            {statusInfo.label}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-5xl px-4 py-6">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold text-neutral-primary">
                {part.title || "Naamloos onderdeel"}
              </h1>
              <Badge
                variant="outline"
                className="bg-neutral-100 font-mono text-xs"
              >
                RENO-{projectId}-O{part.id}
              </Badge>
            </div>
            {part.label && (
              <Badge className="mt-2 border-moss-primary/20 bg-moss-primary/10 text-moss-primary">
                {part.label.name}
              </Badge>
            )}
          </div>

          {/* Unsaved changes indicator */}
          {hasUnsavedChanges && (
            <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">
              Niet-opgeslagen wijzigingen
            </Badge>
          )}
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {/* Row 1: Basic Info + Financial */}
          <div className="grid gap-6 lg:grid-cols-2">
            <PartBasicInfoSection
              part={part}
              onFieldChange={handleFieldChange}
              buildingId={project.building?.id || buildingId || ""}
              onUpdate={refreshPartData}
            />
            <PartFinancialSection part={part} />
          </div>

          {/* Row 1.5: Voorziene kostenplaatsen — picked before any budget */}
          <ProvisionalUnitsSection
            part={part}
            buildingId={project.building?.id || buildingId || ""}
            projectDurationInDays={(() => {
              // Use the locked planning if available; otherwise the
              // BE-computed effective window (which auto-tracks parts).
              const start =
                (project as any)?.effectivePlannedStartDate ??
                (project as any)?.lockedPlannedStartDate;
              const end =
                (project as any)?.effectivePlannedEndDate ??
                (project as any)?.lockedPlannedEndDate;
              if (!start || !end) return null;
              const ms =
                new Date(end).getTime() - new Date(start).getTime();
              if (Number.isNaN(ms) || ms <= 0) return null;
              return Math.ceil(ms / (1000 * 60 * 60 * 24)) + 1;
            })()}
            isPlanningLocked={!!(project as any)?.isPlanningLocked}
            onUpdate={refreshPartData}
          />

          {/* Row 2: Budget Assignment */}
          <PartBudgetAssignmentSection
            part={part}
            buildingId={project.building?.id || buildingId || ""}
            onUpdate={refreshPartData}
          />

          {/* Row 3: Planning */}
          <div className="grid gap-6">
            <PartPlanningSection part={part} onFieldChange={handleFieldChange} />
          </div>

          {/* Row 3: Suppliers (full width) */}
          <PartSuppliersSection
            part={part}
            projectId={projectId || ""}
            onUpdate={refreshPartData}
          />

          {/* Row 4: Financial documents subset scoped to this project part. */}
          <PartFinancialDocumentsSection
            part={part}
            projectId={projectId}
            buildingId={project.building?.id || buildingId}
          />
        </div>
      </div>

      {/* Footer Actions */}
      <div className="sticky bottom-0 border-t border-neutral-200 bg-white/95 backdrop-blur-sm">
        <div className="mx-auto max-w-5xl px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => setShowDeleteDialog(true)}
              className="text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              <Trash className="mr-2 h-4 w-4" />
              Verwijderen
            </Button>

            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={navigateToProject}>
                Annuleren
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving || !hasUnsavedChanges}
                className="bg-moss-primary hover:bg-moss-primary/90"
              >
                {isSaving ? (
                  <>
                    <Spinner className="mr-2 h-4 w-4 animate-spin" />
                    Opslaan...
                  </>
                ) : (
                  <>
                    <FloppyDisk className="mr-2 h-4 w-4" />
                    Opslaan
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Projectonderdeel verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je "{part.title}" wilt verwijderen? Dit kan niet
              ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              Annuleren
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Spinner className="mr-2 h-4 w-4 animate-spin" />
                  Verwijderen...
                </>
              ) : (
                "Verwijderen"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProjectPartDetailPage;
